import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  buildContractDocument,
  can,
  documentFileName,
  lineKmChangeText,
  optionTitle,
  optionsOf,
  parseLineKmChange,
  projectFromContract,
  projectQcColumns,
  qcChanges,
  validateContractForIssue,
  validateProjectSetup,
  localIsoDate,
  validateQcRules,
  withClientSigningTags,
  type Contract,
  type ContractVariation,
  type DocumentBundle,
  type QcRule,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, IssueList, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { browserAssets, downloadPdf } from '../documents/browserAssets.ts';
import { renderPdf } from '../documents/render.ts';
import {
  createProject,
  listContractDocuments,
  listContracts,
  loadContract,
  loadContractHistory,
  loadTenantDocuments,
  recordDocument,
  setContractStatus,
  sha256Hex,
  signContract,
  varyContract,
  type ContractDocumentRow,
  type ContractListRow,
  type QcVersionRow,
  type TenantDocuments,
} from './api.ts';
import { QcRulesEditor } from './QuoteEditor.tsx';
import { ContractApprovalPanel } from '../approvals/ContractApprovalPanel.tsx';

export interface ContractsScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  /** Open this contract straight away (after "Make contract"). */
  readonly initialContractId?: string | null;
}

const STATUS_LABEL: Record<Contract['status'], string> = {
  draft: 'Draft',
  issued: 'Issued',
  signed: 'Signed',
  closed: 'Closed',
};

/**
 * Contracts (TJR inventory 01 §3, §10.5 steps 6–7): the full and operator (OP) PDFs from one source, issue, signing
 * (decision 129), QC variations (decision 122) and project set-up. Field crew and data processors see only the OP
 * documents (decision 121).
 */
export function ContractsScreen({
  sb,
  tenantId,
  userId,
  roles,
  initialContractId,
}: ContractsScreenProps): ReactElement {
  const commercial = can(roles, 'commercial.read');
  if (!commercial) return <OpDocuments sb={sb} tenantId={tenantId} />;
  return (
    <ContractList
      sb={sb}
      tenantId={tenantId}
      userId={userId}
      roles={roles}
      initialContractId={initialContractId ?? null}
    />
  );
}

async function renderAndDownload(bundle: DocumentBundle, fileName: string): Promise<Uint8Array> {
  const pdf = await renderPdf(bundle, browserAssets);
  downloadPdf(pdf, fileName);
  return pdf;
}

/** Field crew and data processors: the OP contracts issued to them. */
function OpDocuments({
  sb,
  tenantId,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
}): ReactElement {
  const [rows, setRows] = useState<ContractDocumentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const reload = useCallback(() => {
    setError(null);
    listContractDocuments(sb, tenantId)
      .then((r) => setRows(r.filter((d) => d.variant === 'op')))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);
  return (
    <section className="page">
      <PageHeader
        title="Contracts"
        subtitle="Operator (OP) copies of the contracts: specifications, contacts and conditions."
      />
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="contracts" />}
      {rows && rows.length === 0 && (
        <div className="panel">
          <p className="body">No OP contract has been issued yet.</p>
        </div>
      )}
      {rows && rows.length > 0 && (
        <ul className="stack list-plain">
          {rows.map((d) => (
            <li key={d.id} className="row row--between panel">
              <span className="data">{d.file_name}</span>
              <Button
                busy={busy === d.id}
                onClick={() => {
                  setBusy(d.id);
                  void renderAndDownload(d.bundle as DocumentBundle, d.file_name)
                    .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
                    .finally(() => setBusy(null));
                }}
              >
                {busy === d.id ? 'Preparing…' : 'Download'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ContractList({
  sb,
  tenantId,
  userId,
  roles,
  initialContractId,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  readonly initialContractId: string | null;
}): ReactElement {
  const [rows, setRows] = useState<ContractListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(initialContractId);
  const reload = useCallback(() => {
    setError(null);
    listContracts(sb, tenantId)
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  if (open) {
    return (
      <ContractView
        sb={sb}
        tenantId={tenantId}
        userId={userId}
        roles={roles}
        contractId={open}
        onBack={() => {
          setOpen(null);
          reload();
        }}
      />
    );
  }
  return (
    <section className="page">
      <PageHeader
        title="Contracts"
        subtitle="Contracts made from accepted quotes. Make one from a quote's Review step."
      />
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="contracts" />}
      {rows && rows.length === 0 && (
        <div className="panel">
          <p className="body">No contracts yet. Accept a quote revision, then press "Make contract".</p>
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Job</th>
                <th className="label">From quote revision</th>
                <th className="label">Date</th>
                <th className="label">Status</th>
                <th className="label">Project</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <button type="button" className="link data" onClick={() => setOpen(c.id)}>
                      {c.number}
                    </button>
                  </td>
                  <td className="data">{c.quote_revision}</td>
                  <td className="data">{c.contract_date}</td>
                  <td>
                    <span className="chip caption">{STATUS_LABEL[c.status]}</span>
                  </td>
                  <td>{c.project_id ? 'Created' : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ContractView({
  sb,
  tenantId,
  userId,
  roles,
  contractId,
  onBack,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  readonly contractId: string;
  readonly onBack: () => void;
}): ReactElement {
  const [contract, setContract] = useState<Contract | null>(null);
  const [docs, setDocs] = useState<TenantDocuments | null>(null);
  const [history, setHistory] = useState<{ variations: ContractVariation[]; qc: QcVersionRow[] } | null>(
    null,
  );
  const [documents, setDocuments] = useState<ContractDocumentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  // R191: "Record signing" filled in from the client's DocuSeal signing
  const [prefill, setPrefill] = useState<{ readonly date: string; readonly file: string } | null>(null);
  const [varying, setVarying] = useState(false);
  const write = can(roles, 'contract.write');

  const reload = useCallback(() => {
    setError(null);
    Promise.all([
      loadContract(sb, contractId),
      loadTenantDocuments(sb, tenantId),
      loadContractHistory(sb, contractId),
      listContractDocuments(sb, tenantId, contractId),
    ])
      .then(([c, d, h, docRows]) => {
        setContract(c);
        setDocs(d);
        setHistory(h);
        setDocuments(docRows);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, contractId, tenantId]);
  useEffect(reload, [reload]);

  if (error) {
    return (
      <section className="page">
        <ErrorState message={error} onRetry={reload} />
        <Button onClick={onBack}>Back to contracts</Button>
      </section>
    );
  }
  if (!contract || !docs || !history) {
    return (
      <section className="page">
        <Loading what="the contract" />
      </section>
    );
  }
  const agg = contract.snapshot.quote;
  // decision 171: the option of the revision the contract was made from
  const option = optionsOf(agg).find((o) => o.id === contract.option_id);
  const optionText = option && (option.label !== 'A' || option.description) ? `, ${optionTitle(option)}` : '';
  const currentQc: QcRule[] = history.qc[history.qc.length - 1]?.rules ?? agg.qc.map((r) => ({ ...r }));
  const issueChecks = validateContractForIssue(agg);

  async function act(name: string, action: () => Promise<string>): Promise<void> {
    setBusy(name);
    setNotice(null);
    try {
      setNotice({ ok: true, text: await action() });
      reload();
    } catch (e: unknown) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  const download = (variant: 'full' | 'op'): Promise<void> =>
    act(variant, async () => {
      if (!docs.profile)
        throw new Error(
          'The company details are not set up. The tenant admin enters them in Company, Details and payment.',
        );
      if (!contract) throw new Error('No contract.');
      const bundle = buildContractDocument(variant, contract.snapshot, {
        profile: docs.profile,
        payment: docs.payment,
        jobNumber: contract.number,
        contractDate: contract.contract_date,
      });
      const fileName = documentFileName(agg, contract.number, variant);
      const pdf = await renderAndDownload(bundle, fileName);
      if (write) {
        const revision = documents.filter((d) => d.variant === variant).length + 1;
        await recordDocument(
          sb,
          {
            tenant_id: tenantId,
            contract_id: contract.id,
            variant,
            revision,
            file_name: fileName,
            bundle,
            sha256: await sha256Hex(pdf),
          },
          userId,
        );
      }
      return `${fileName} is ready.${variant === 'op' && write ? ' Field crew can now download it from Contracts.' : ''}`;
    });

  return (
    <section className="page">
      <PageHeader
        title={`Contract ${contract.number}`}
        subtitle={`${agg.client?.name ?? ''} · ${agg.quote.project_name} · from ${agg.quote.number} revision ${contract.quote_revision}${optionText} · ${STATUS_LABEL[contract.status]}`}
      >
        <Button variant="quiet" onClick={onBack}>
          Back to contracts
        </Button>
      </PageHeader>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      <div className="panel stack">
        <h2 className="heading">Documents</h2>
        <p className="body muted">
          The full contract has every amount and the bank details. The operator (OP) copy is the same contract
          with the amounts and the bank details left blank; it keeps the payment percentages.
        </p>
        <div className="row row--wrap">
          <Button busy={busy === 'full'} onClick={() => void download('full')}>
            {busy === 'full' ? 'Preparing…' : 'Download full contract'}
          </Button>
          <Button busy={busy === 'op'} onClick={() => void download('op')}>
            {busy === 'op' ? 'Preparing…' : 'Download OP contract'}
          </Button>
        </div>
        {documents.length > 0 && (
          <p className="caption muted">
            Made so far: {documents.map((d) => `${d.file_name} (${d.created_at.slice(0, 10)})`).join(', ')}
          </p>
        )}
      </div>

      {write && contract.status === 'draft' && (
        <div className="panel stack">
          <h2 className="heading">Issue</h2>
          <IssueList title="To fix in the quote before issuing" issues={issueChecks} />
          <Button
            variant="primary"
            disabled={issueChecks.length > 0}
            busy={busy === 'issue'}
            onClick={() =>
              void act('issue', async () => {
                await setContractStatus(sb, contract.id, 'issued', userId);
                return 'The contract is issued. Record the signing when the signed copy comes back.';
              })
            }
          >
            Mark as issued
          </Button>
        </div>
      )}

      {write && (contract.status === 'draft' || contract.status === 'issued') && (
        <div className="panel stack">
          <h2 className="heading">Signing</h2>
          {!signing ? (
            <Button onClick={() => setSigning(true)}>Record signing</Button>
          ) : (
            <SignForm
              {...(prefill ? { initialDate: prefill.date, initialFile: prefill.file } : {})}
              onCancel={() => setSigning(false)}
              onSign={(date, file) =>
                void act('sign', async () => {
                  await signContract(sb, contract.id, date, file);
                  setSigning(false);
                  return `Signed on ${date}. The contract is now kept as it is; changes are variations.`;
                })
              }
            />
          )}
        </div>
      )}

      {contract.status !== 'draft' || write ? (
        <ContractApprovalPanel
          sb={sb}
          tenantId={tenantId}
          userId={userId}
          contract={{ id: contract.id, number: contract.number, status: contract.status }}
          write={write}
          makePdf={async () => {
            if (!docs.profile)
              throw new Error(
                'The company details are not set up. The tenant admin enters them in Company, Details and payment.',
              );
            const bundle = withClientSigningTags(
              buildContractDocument('full', contract.snapshot, {
                profile: docs.profile,
                payment: docs.payment,
                jobNumber: contract.number,
                contractDate: contract.contract_date,
              }),
            );
            return {
              pdf: await renderPdf(bundle, browserAssets),
              fileName: documentFileName(agg, contract.number, 'full'),
            };
          }}
          onRecordSigning={(date, file) => {
            setPrefill({ date, file });
            setSigning(true);
          }}
        />
      ) : null}

      {contract.status === 'signed' && (
        <div className="panel stack">
          <h2 className="heading">Signed</h2>
          <p className="body">
            Signed on <span className="data">{contract.signed_at}</span>
            {contract.signed_file_name ? ` · ${contract.signed_file_name}` : ''}.
          </p>
          {write && !contract.project_id && (
            <CreateProject
              onCreate={() =>
                void act('project', async () => {
                  const problems = validateProjectSetup(contract.snapshot);
                  if (problems.length) throw new Error(problems[0]!.message);
                  const setup = projectFromContract(contract.snapshot, contract.number);
                  const latest = projectQcColumns(currentQc);
                  await createProject(sb, contract.id, {
                    ...setup,
                    project: { ...setup.project, ...latest },
                  });
                  return `Project ${contract.number} is created with ${setup.blocks.length} blocks and the contract's QC limits. Open it in Teslyn Field to import the final flight plan.`;
                })
              }
              busy={busy === 'project'}
              blocks={agg.areas.length}
              number={contract.number}
            />
          )}
          {contract.project_id && (
            <p className="body">Project {contract.number} has been created from this contract.</p>
          )}
        </div>
      )}

      <div className="panel stack">
        <h2 className="heading">QC limits and variations</h2>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="label">Version</th>
                <th className="label">From</th>
                <th className="label">Reason</th>
                <th className="label">Date</th>
              </tr>
            </thead>
            <tbody>
              {history.qc.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    Version 1 is kept when the signing is recorded.
                  </td>
                </tr>
              )}
              {history.qc.map((v) => (
                <tr key={v.version}>
                  <td className="data">{v.version}</td>
                  <td>{v.source === 'contract' ? 'Signed contract' : 'Variation'}</td>
                  <td>{v.reason || '–'}</td>
                  <td className="data">{v.created_at.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {history.variations.length > 0 && (
          <ul className="stack">
            {history.variations.map((v) => (
              <li key={v.id} className="body">
                Variation {v.number} ({v.date}): {v.summary}. {v.reason}{' '}
                {v.line_km_change === null || v.line_km_change === undefined ? (
                  'Line km unchanged.'
                ) : (
                  <>
                    Line km change <span className="data">{lineKmChangeText(v.line_km_change)}</span>.
                  </>
                )}
                {v.client_agreed
                  ? ' Agreed by the client.'
                  : ' Not agreed by the client yet, so the Operations Hub does not count it.'}
              </li>
            ))}
          </ul>
        )}
        {write && contract.status === 'signed' && !varying && (
          <Button onClick={() => setVarying(true)}>Record a variation</Button>
        )}
        {varying && (
          <VaryContract
            current={currentQc}
            busy={busy === 'vary'}
            onCancel={() => setVarying(false)}
            onSave={(rules, summary, reason, clientAgreed, lineKmChange) =>
              void act('vary', async () => {
                await varyContract(sb, {
                  contractId: contract.id,
                  rules,
                  summary,
                  reason,
                  clientAgreed,
                  changes: rules ? qcChanges(currentQc, rules) : [],
                  columns: rules ? projectQcColumns(rules) : null,
                  lineKmChange,
                });
                setVarying(false);
                return rules
                  ? 'The variation is recorded with a new QC version. Reports name the version they used.'
                  : 'The variation is recorded. The Operations Hub counts its line km once the client has agreed it.';
              })
            }
          />
        )}
      </div>
    </section>
  );
}

function SignForm({
  onSign,
  onCancel,
  initialDate,
  initialFile,
}: {
  readonly onSign: (date: string, file: string) => void;
  readonly onCancel: () => void;
  /** Filled in from the client's DocuSeal signing (R191). */
  readonly initialDate?: string;
  readonly initialFile?: string;
}): ReactElement {
  const [date, setDate] = useState(initialDate ?? localIsoDate());
  const [file, setFile] = useState(initialFile ?? '');
  return (
    <form
      className="stack"
      aria-label="Record signing"
      onSubmit={(e) => {
        e.preventDefault();
        onSign(date, file);
      }}
    >
      <div className="form-grid">
        <TextField label="Signing date" type="date" value={date} onChange={setDate} />
        <TextField
          label="Signed copy file name"
          spellCheck={false}
          hint="The signed PDF as filed, for example F26048_Contract_signed.pdf."
          value={file}
          onChange={setFile}
        />
      </div>
      <div className="row">
        <Button type="submit" variant="primary">
          Record signing
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function CreateProject({
  onCreate,
  busy,
  blocks,
  number,
}: {
  readonly onCreate: () => void;
  readonly busy: boolean;
  readonly blocks: number;
  readonly number: string;
}): ReactElement {
  return (
    <div className="stack">
      <p className="body">
        Create project <span className="data">{number}</span>: the {blocks} contracted area
        {blocks === 1 ? '' : 's'} become blocks with their spacing, direction and survey height, and the QC
        limits go to the project settings Field uses.
      </p>
      <Button variant="primary" busy={busy} onClick={onCreate}>
        {busy ? 'Creating…' : 'Create project'}
      </Button>
    </div>
  );
}

/**
 * A contract variation (decisions 122 and 258): a change of the QC limits, of the contract's line km, or both, with
 * the reason. The line km change is signed (+ or −); blank when the km do not change.
 */
function VaryContract({
  current,
  busy,
  onSave,
  onCancel,
}: {
  readonly current: readonly QcRule[];
  readonly busy: boolean;
  readonly onSave: (
    rules: QcRule[] | null,
    summary: string,
    reason: string,
    clientAgreed: boolean,
    lineKmChange: number | null,
  ) => void;
  readonly onCancel: () => void;
}): ReactElement {
  const [rules, setRules] = useState<QcRule[]>(current.map((r) => ({ ...r })));
  const [km, setKm] = useState('');
  const [reason, setReason] = useState('');
  const [clientAgreed, setClientAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changes = qcChanges(current, rules);
  const issues = validateQcRules(rules);
  const kmParsed = parseLineKmChange(km);
  const kmLine =
    kmParsed.ok && kmParsed.value !== null ? `Line km: ${lineKmChangeText(kmParsed.value)}` : null;
  return (
    <form
      className="stack"
      aria-label="Record a variation"
      onSubmit={(e) => {
        e.preventDefault();
        if (!kmParsed.ok) return setError(kmParsed.message);
        if (changes.length === 0 && kmParsed.value === null)
          return setError('Nothing has changed: change a QC limit or enter the line km change.');
        if (!reason.trim()) return setError('Give the reason for the change.');
        if (changes.length > 0 && issues.length) return setError(issues[0]!.message);
        const summary = [...changes, ...(kmLine ? [kmLine] : [])].join('; ');
        onSave(changes.length > 0 ? rules : null, summary, reason.trim(), clientAgreed, kmParsed.value);
      }}
    >
      <QcRulesEditor rules={rules} onChange={setRules} issues={issues} />
      <TextField
        label="Line km change (km)"
        spellCheck={false}
        hint="The change of the contract's line km: for example -1640 when an area is dropped, 500 when one is added. Leave it blank when the line km do not change."
        value={km}
        onChange={setKm}
      />
      {(changes.length > 0 || kmLine) && (
        <ul className="stack">
          {changes.map((c) => (
            <li key={c} className="body">
              {c}
            </li>
          ))}
          {kmLine && <li className="body">{kmLine}</li>}
        </ul>
      )}
      <TextArea label="Reason" rows={2} value={reason} onChange={setReason} />
      <CheckField
        label="The client agreed to this change"
        checked={clientAgreed}
        onChange={setClientAgreed}
      />
      {error && (
        <p className="field__error caption" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          Record variation
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
